import { useTheme as useThemeSC } from "styled-components"
import type { SpecifiedTheme, ThemeOptions, Theme, Spacing, SpacingConfig, Media, BreakPoints } from "./theme.types"

type Points = "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl"

const makeBreakPoints = (media: Media): BreakPoints => {
  const breakPoints: Partial<BreakPoints> = {}
  Object.keys(media).forEach((it) => {
    breakPoints[it as Points] = {
      up: `@media(min-width: ${media[it as Points]}px)`,
      down: `@media(max-width: ${media[it as Points]}px)`,
    }
  })
  return breakPoints as BreakPoints
}

/**
 * Generates a theme to supply to the theme provider
 * @param theme
 * @param options
 */
export const makeTheme = (theme: SpecifiedTheme, options: ThemeOptions): Theme => {
  const spacingConfig = options.spacing as SpacingConfig
  Object.keys(options.spacing).forEach((it) => {
    const positiveVal = spacingConfig[Number(it) as Spacing]
    spacingConfig[(Number(it) * -1) as Spacing] = positiveVal ? positiveVal * -1 : undefined
  })

  const pxToEm = (px: number) => px / options.defaultPxSize
  const spacing = (allOrTop: Spacing, bottomOrSidesOrRight?: Spacing, bottom?: Spacing, left?: Spacing) => {
    const make = (val: Spacing) => {
      if (!val) return `0`
      const value = spacingConfig[val]
      if (!value) return `0`
      return `${pxToEm(value)}rem`
    }
    let value = make(allOrTop)
    if (bottomOrSidesOrRight === undefined) return value
    value += ` ${make(bottomOrSidesOrRight)}`
    if (bottom === undefined) return value
    value += ` ${make(bottom)}`
    if (left === undefined) return value
    value += ` ${make(left)}`
    return value
  }
  return {
    ...theme,
    fontSize: {
      large: `${pxToEm(options.fontSize.LARGE)}rem`,
      title: `${pxToEm(options.fontSize.TITLE)}rem`,
      subtitle: `${pxToEm(options.fontSize.SUBTITLE)}rem`,
      body: `1rem`,
      caption: `${pxToEm(options.fontSize.CAPTION)}rem`,
    },
    breakPoints: makeBreakPoints(theme.media),
    spacing,
    pxToEm: (px: number) => `${pxToEm(px)}rem`,
    defaultBodySize: options.defaultPxSize,
  }
}

export const useTheme = () => useThemeSC() as Theme
